import React from 'react';
import useCondition from '../../../useCondition';
import {useSalesforceDealRecordTypeQuery} from 'api';
import {XMarkIcon} from '@heroicons/react/20/solid';
import SelectOption from './SelectOption';
import MultiSelectOption from './MultiSelectOption';

type SelectSearchCondition = {
  field_select_option_id?: string;
  field_type: string;
  deal_field_id: string;
};

type MultiSelectSearchCondition = {
  values?: string[];
  field_type: string;
  deal_field_id: string;
};

type SearchCondition = SelectSearchCondition | MultiSelectSearchCondition;

interface Props {
  searchCondition: SearchCondition;
  setSearchCondition: (condition: SearchCondition) => void;
  onDelete: () => void;
}

const SalesforceDealConditionField = ({
  searchCondition,
  setSearchCondition,
  onDelete,
}: Props) => {
  const {condition} = useCondition();

  const {
    data: {salesforceDealRecordType: {salesforceDealFields = []} = {}} = {},
  } = useSalesforceDealRecordTypeQuery({
    variables: {
      salesforceDealRecordTypeId: condition.salesforceDealRecordTypeId,
    },
    skip: !condition.salesforceDealRecordTypeId,
  });

  const targetFields = salesforceDealFields
    .filter(
      (item) =>
        (item.fieldType === 'select' || item.fieldType === 'multi_select') &&
        item.fieldName.endsWith('__c'),
    )
    .filter(
      (item) =>
        !condition.salesforceDealSearchConditions?.some(
          (cond) =>
            cond.deal_field_id === item.id &&
            searchCondition.deal_field_id !== item.id,
        ),
    );

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const dealField = targetFields.find((item) => item.id === e.target.value);
    let condition: MultiSelectSearchCondition = {
      field_type: dealField?.fieldType || '',
      deal_field_id: dealField?.id || '',
    };

    if (!dealField) {
      setSearchCondition({...condition});
      return;
    }

    if (dealField.fieldType === 'select') {
      condition = {
        ...condition,
        field_select_option_id: '',
      } as SelectSearchCondition;
    }

    if (dealField.fieldType === 'multi_select') {
      condition = {
        ...condition,
        values: [],
      } as MultiSelectSearchCondition;
    }

    setSearchCondition({
      ...condition,
    });
  };

  return (
    <div className="flex gap-2">
      <select
        className="form-select border-c-border rounded text-base w-64 h-[44px]"
        onChange={onChange}
        value={searchCondition.deal_field_id}>
        <option value="" />
        {targetFields.map((field) => (
          <option value={field.id} key={field.id}>
            {field.label}
          </option>
        ))}
      </select>
      {searchCondition.field_type === 'multi_select' && (
        <MultiSelectOption
          fieldId={searchCondition.deal_field_id}
          values={(searchCondition as MultiSelectSearchCondition).values}
          onChange={(values) =>
            setSearchCondition({
              ...searchCondition,
              values,
            })
          }
        />
      )}
      {searchCondition.field_type === 'select' && (
        <SelectOption
          fieldId={searchCondition.deal_field_id}
          value={
            (searchCondition as SelectSearchCondition).field_select_option_id
          }
          onChange={(value) =>
            setSearchCondition({
              ...searchCondition,
              field_select_option_id: value,
            })
          }
        />
      )}
      <button
        onClick={onDelete}
        className="bg-white px-0 py-6 h-5 w-5 flex items-center justify-center rounded-full cursor-pointer">
        <XMarkIcon className="w-5 h-5 text-c-light" />
      </button>
    </div>
  );
};

export default SalesforceDealConditionField;
